import { CoolLocalStorage } from '@angular-cool/storage';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { GenesysCloudConfigService } from 'common/genesys-cloud-config/genesys-cloud-config.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TranslationLoaderService } from 'shared/services/translation-loader/translation-loader.service';
import { WindowService } from 'shared/services/window/window.service';
import { WorkItemStateHandler } from 'shared/services/workitem-state-handler/workitem-state-handler.service';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'app-genesys-cobrowse-box',
  templateUrl: './genesys-cobrowse-box.component.html',
  styleUrls: ['./genesys-cobrowse-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenesysCobrowseBoxComponent implements OnInit, OnDestroy {
  private readonly onDestroySubject = new Subject<void>();

  @Input()
  public conversationId: string;

  @Input()
  public isExpandedMode: boolean;

  @Output()
  readonly toggleCobrowse = new EventEmitter<string>();

  private readonly _messageListener: (event: MessageEvent) => void;
  cobrowseUrl = '';
  genesysCloudDomain = '';
  cobrowseFullScreen = false;

  @ViewChild('cobrowseFrame', { read: ElementRef }) cobrowseFrame: ElementRef;

  constructor(
    private readonly genesysCloudConfigService: GenesysCloudConfigService,
    private readonly localStorageService: CoolLocalStorage,
    private readonly translationLoader: TranslationLoaderService,
    private $window: WindowService,
    private readonly workItemStateHandler: WorkItemStateHandler,
    readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(DOCUMENT) private document: Document
  ) {
    this._messageListener = (event: MessageEvent) => this._receiveMessageFromIframe(event);
    this.workItemStateHandler.workItemFocused$
      .pipe(takeUntil(this.onDestroySubject))
      .subscribe(() => changeDetectorRef.detectChanges());
  }

  ngOnInit(): void {
    this.conversationChange(this.conversationId);

    this.$window.addEventListener('message', (event: MessageEvent) => {
      this._messageListener(event);
    });
  }

  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  conversationChange(conversationId: string): void {
    this.genesysCloudDomain = this.genesysCloudConfigService.determineGenesysCloudDomainFromHostname();
    this.cobrowseUrl = this._parseURL(conversationId);
  }

  get isFocused(): boolean {
    return this.workItemStateHandler.isFocused(this.conversationId);
  }

  private _parseURL(conversationId: string): string {
    const locale = this.localStorageService.getItem(this.translationLoader.preferredLanguageKey);
    const url = `https://apps.${
      this.genesysCloudDomain
    }/cobrowse-next/viewer.html?lang=${locale.toLocaleLowerCase()}&env=${
      this.genesysCloudDomain
    }&conversationId=${conversationId}&usePopupAuth=false`;
    return url;
  }

  private _receiveMessageFromIframe(event: MessageEvent): void {
    if (this._validateEventSource(event)) {
      switch (event.data.action) {
        case 'closeScreenShare':
          this.toggleCobrowse.emit(this.conversationId);
          break;
        case 'fullscreen':
          if (this.cobrowseFullScreen) {
            this.document.exitFullscreen();
          } else {
            this.document.getElementsByClassName('gc-cobrowse-box')[0].requestFullscreen();
          }
          this.cobrowseFullScreen = !this.cobrowseFullScreen;
          break;
      }
    }
  }

  private _validateEventSource(event: MessageEvent): boolean {
    const origin = new URL(this.cobrowseUrl).origin;
    return event && event.origin === origin;
  }
}
